import type { StoreApi } from 'zustand';
import { superAdminApi } from '../api/superAdminApi';
import { getAdminApiError } from '../lib/adminApiErrors';
import type {
  AdminApiError,
  AdminBulkResponse,
  AdminUserDto,
} from '../types';
import type { SuperAdminActions, SuperAdminState } from './useSuperAdminStore';

type AdminStore = SuperAdminState & SuperAdminActions;
type SetAdminStore = StoreApi<AdminStore>['setState'];
type UserCollectionVersion = { current: number };
type UserRequestActivity = ReturnType<typeof createUserRequestActivity>;
type UserActionKeys =
  | 'fetchUsers'
  | 'loadUserDetail'
  | 'clearUserDetail'
  | 'createUser'
  | 'saveUser'
  | 'deleteUser'
  | 'resetUserPassword'
  | 'bulkUsers';

const upsertUser = (
  users: readonly AdminUserDto[],
  canonical: AdminUserDto
): AdminUserDto[] => {
  const next = users.some((user) => user.id === canonical.id)
    ? users.map((user) => user.id === canonical.id ? canonical : user)
    : [...users, canonical];
  return next.sort((left, right) => left.username.localeCompare(
    right.username,
    undefined,
    { sensitivity: 'base' }
  ) || left.id - right.id);
};

const createUserRequestActivity = (set: SetAdminStore) => {
  let pendingRequests = 0;
  return {
    begin: () => {
      pendingRequests += 1;
      set({ userLoading: true, userError: null });
    },
    finish: () => {
      pendingRequests = Math.max(0, pendingRequests - 1);
      set({ userLoading: pendingRequests > 0 });
    },
  };
};

const fetchUsersAction = (
  set: SetAdminStore,
  activity: UserRequestActivity,
  version: UserCollectionVersion
) => async () => {
  const requestVersion = version.current;
  activity.begin();
  try {
    const users = await superAdminApi.getUsers();
    if (requestVersion === version.current) {
      set({ users });
    }
  } catch (error) {
    if (requestVersion === version.current) {
      set({ userError: getAdminApiError(error) });
    }
  } finally {
    activity.finish();
  }
};

const createUserDetailActions = (set: SetAdminStore) => {
  let requestGeneration = 0;
  const loadUserDetail = async (id: number) => {
    const generation = ++requestGeneration;
    set({ userDetail: null, userLoading: true, userError: null });
    try {
      const userDetail = await superAdminApi.getUser(id);
      if (generation === requestGeneration) {
        set({ userDetail, userLoading: false });
      }
      return userDetail;
    } catch (error) {
      if (generation === requestGeneration) {
        set({
          userError: getAdminApiError(error, 'user_detail_failed'),
          userLoading: false,
        });
      }
      throw error;
    }
  };
  const clearUserDetail = () => {
    requestGeneration += 1;
    set({ userDetail: null, userLoading: false, userError: null });
  };
  return { loadUserDetail, clearUserDetail };
};

const createUserAction: (
  set: SetAdminStore,
  activity: UserRequestActivity,
  version: UserCollectionVersion
) => SuperAdminActions['createUser'] = (set, activity, version) => async (request) => {
  activity.begin();
  try {
    const created = await superAdminApi.createUser(request);
    version.current += 1;
    set((state) => ({
      users: upsertUser(state.users, created),
      userDetail: created,
    }));
    return created;
  } catch (error) {
    set({
      userError: getAdminApiError(error, 'user_invite_failed'),
    });
    throw error;
  } finally {
    activity.finish();
  }
};

const saveUserAction: (
  set: SetAdminStore,
  activity: UserRequestActivity,
  version: UserCollectionVersion
) => SuperAdminActions['saveUser'] = (set, activity, version) => async (id, patch) => {
  activity.begin();
  try {
    const canonical = await superAdminApi.updateUser(id, patch);
    version.current += 1;
    set((state) => ({
      users: upsertUser(state.users, canonical),
      userDetail: state.userDetail?.id === id ? canonical : state.userDetail,
    }));
    return canonical;
  } catch (error) {
    set({
      userError: getAdminApiError(error, 'user_update_failed'),
    });
    throw error;
  } finally {
    activity.finish();
  }
};

const resetUserPasswordAction: (
  set: SetAdminStore,
  activity: UserRequestActivity
) => SuperAdminActions['resetUserPassword'] = (set, activity) => async (id) => {
  activity.begin();
  try {
    await superAdminApi.resetUserPassword(id);
  } catch (error) {
    set({
      userError: getAdminApiError(error, 'user_reset_password_failed'),
    });
    throw error;
  } finally {
    activity.finish();
  }
};

const applyBulkSuccesses = (
  users: readonly AdminUserDto[],
  action: Parameters<SuperAdminActions['bulkUsers']>[0],
  succeededIds: readonly number[]
): AdminUserDto[] => {
  const succeeded = new Set(succeededIds);
  if (action === 'delete') {
    return users.filter((user) => !succeeded.has(user.id));
  }
  const status = action === 'block' ? 'blocked' : 'active';
  return users.map((user) => succeeded.has(user.id) ? { ...user, status } : user);
};

const applyBulkToDetail = (
  detail: AdminUserDto | null,
  action: Parameters<SuperAdminActions['bulkUsers']>[0],
  succeededIds: readonly number[]
): AdminUserDto | null => {
  if (!detail || !succeededIds.includes(detail.id)) {
    return detail;
  }
  if (action === 'delete') {
    return null;
  }
  return { ...detail, status: action === 'block' ? 'blocked' : 'active' };
};

const refreshUsers = async (): Promise<
  { users: AdminUserDto[]; error: null }
  | { users: null; error: AdminApiError }
> => {
  try {
    return { users: await superAdminApi.getUsers(), error: null };
  } catch (error) {
    return {
      users: null,
      error: getAdminApiError(error, 'user_refresh_failed'),
    };
  }
};

const deleteUserAction: (
  set: SetAdminStore,
  activity: UserRequestActivity,
  version: UserCollectionVersion
) => SuperAdminActions['deleteUser'] = (set, activity, version) => async (id) => {
  activity.begin();
  try {
    await superAdminApi.deleteUser(id);
    const mutationVersion = ++version.current;
    set((state) => ({
      users: state.users.filter((user) => user.id !== id),
      userDetail: state.userDetail?.id === id ? null : state.userDetail,
    }));

    const refresh = await refreshUsers();
    if (refresh.users) {
      if (mutationVersion === version.current) {
        set({ users: refresh.users });
      }
      return { succeeded: true, refreshError: null };
    }
    if (mutationVersion === version.current) {
      set({ userError: refresh.error });
    }
    return { succeeded: true, refreshError: refresh.error };
  } catch (error) {
    set({ userError: getAdminApiError(error, 'user_delete_failed') });
    throw error;
  } finally {
    activity.finish();
  }
};

const bulkUsersAction: (
  set: SetAdminStore,
  activity: UserRequestActivity,
  version: UserCollectionVersion
) => SuperAdminActions['bulkUsers'] = (set, activity, version) => async (action, ids) => {
  activity.begin();
  try {
    const result: AdminBulkResponse = await superAdminApi.bulkUsers(action, ids);
    const mutationVersion = ++version.current;
    set((state) => ({
      users: applyBulkSuccesses(state.users, action, result.succeeded),
      userDetail: applyBulkToDetail(
        state.userDetail,
        action,
        result.succeeded
      ),
    }));

    const refresh = await refreshUsers();
    if (refresh.users) {
      if (mutationVersion === version.current) {
        set({ users: refresh.users });
      }
      return { ...result, refreshError: null };
    }
    if (mutationVersion === version.current) {
      set({ userError: refresh.error });
    }
    return { ...result, refreshError: refresh.error };
  } catch (error) {
    set({ userError: getAdminApiError(error, 'user_bulk_failed') });
    throw error;
  } finally {
    activity.finish();
  }
};

export const createUserStoreActions = (
  set: SetAdminStore
): Pick<SuperAdminActions, UserActionKeys> => {
  const activity = createUserRequestActivity(set);
  const version = { current: 0 };
  const detailActions = createUserDetailActions(set);
  return {
    fetchUsers: fetchUsersAction(set, activity, version),
    ...detailActions,
    createUser: createUserAction(set, activity, version),
    saveUser: saveUserAction(set, activity, version),
    deleteUser: deleteUserAction(set, activity, version),
    resetUserPassword: resetUserPasswordAction(set, activity),
    bulkUsers: bulkUsersAction(set, activity, version),
  };
};
